import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import MemberBottomNav from '../../../components/MemberBottomNav'
import {
  CalendarBlank,
  MapPin,
  Bell,
  CaretDown,
  ArrowUpRight
} from '@phosphor-icons/react'

export default function MemberEvents() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [activeEvents, setActiveEvents] = useState([])
  const [pastEvents, setPastEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('upcoming')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  useEffect(() => {
    loadEvents()
  }, [selectedMonth, selectedYear])

  const loadEvents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()

      setUserData(userDataResult)

      const now = new Date()
      const todayDate = now.toISOString().split('T')[0]
      const currentTime = now.toTimeString().slice(0, 5)

      const { data: allEvents } = await supabase
        .from('events')
        .select('*')
        .eq('org_id', userDataResult.org_id)
        .order('start_date', { ascending: true })
        .order('start_time', { ascending: true })

      const upcoming = []
      const active = []
      const past = []

      for (const event of allEvents || []) {
        const eventDate = event.start_date
        const startTime = event.start_time
        const endTime = event.end_time

        let category = 'past'

        if (eventDate > todayDate) {
          category = 'upcoming'
        } else if (eventDate === todayDate) {
          if (startTime > currentTime) {
            category = 'upcoming'
          } else if (endTime > currentTime) {
            category = 'active'
          } else {
            category = 'past'
          }
        }

        const { data: attendance } = await supabase
          .from('attendance_records')
          .select('*')
          .eq('event_id', event.id)
          .eq('user_id', user.id)
          .maybeSingle()

        let status = 'pending'
        if (attendance) {
          status = 'attended'
        } else {
          const { data: confirmation } = await supabase
            .from('event_confirmations')
            .select('status')
            .eq('event_id', event.id)
            .eq('user_id', user.id)
            .maybeSingle()

          if (category === 'past') {
            if (confirmation?.status === 'declined') {
              status = 'declined'
            } else {
              status = 'missed'
            }
          } else {
            if (confirmation?.status === 'confirmed') {
              status = 'confirmed'
            } else if (confirmation?.status === 'declined') {
              status = 'declined'
            } else {
              status = 'pending'
            }
          }
        }

        const eventWithStatus = { ...event, status, category }

        if (category === 'upcoming') {
          upcoming.push(eventWithStatus)
        } else if (category === 'active') {
          active.push(eventWithStatus)
        } else {
          past.push(eventWithStatus)
        }
      }

      setUpcomingEvents(upcoming)
      setActiveEvents(active)
      setPastEvents(past.reverse())

    } catch (error) {
      console.error('Load events error:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status, isPastEvent = false) => {
    if (isPastEvent) {
      if (status === 'attended') {
        return (
          <span className="px-3 py-1 rounded-full outline outline-1 outline-[#BFE0C8] text-xs font-normal bg-[#EAF5ED] text-[#2B9646]">
            Attended
          </span>
        )
      } else if (status === 'missed') {
        return (
          <span className="px-3 py-1 rounded-full outline outline-1 outline-[#FBD3C2] text-xs font-normal bg-[#FEF0EB] text-[#A94C25]">
            Missed
          </span>
        )
      } else if (status === 'declined') {
        return (
          <span className="px-3 py-1 rounded-full outline outline-1 outline-[#FBD3C2] text-xs font-normal bg-[#FEF0EB] text-[#A94C25]">
            Declined
          </span>
        )
      } else {
        return (
          <span className="px-3 py-1 rounded-full text-xs outline outline-1 outline-[#E2E8F0] font-normal bg-[#F8FAFC] text-[#64748B]">
            Pending
          </span>
        )
      }
    }

    if (status === 'attended') {
      return (
        <span className="px-3 py-1 rounded-full outline outline-1 outline-[#BFE0C8] text-xs font-normal bg-[#EAF5ED] text-[#2B9646]">
          Attended
        </span>
      )
    } else if (status === 'confirmed') {
      return (
        <span className="px-3 py-1 rounded-full outline outline-1 outline-[#BFE0C8] text-xs font-normal bg-[#EAF5ED] text-[#2B9646]">
          Confirmed
        </span>
      )
    } else if (status === 'declined') {
      return (
        <span className="px-3 py-1 rounded-full outline outline-1 outline-[#FBD3C2] text-xs font-normal bg-[#FEF0EB] text-[#A94C25]">
          Declined
        </span>
      )
    } else if (status === 'pending') {
      return (
        <span className="px-3 py-1 rounded-full text-xs outline outline-1 outline-[#E2E8F0] font-normal bg-[#F8FAFC] text-[#64748B]">
          Pending
        </span>
      )
    }

    return (
      <span className="px-3 py-1 rounded-full text-xs outline outline-1 outline-[#E2E8F0] font-normal bg-[#F8FAFC] text-[#64748B]">
        Pending
      </span>
    )
  }

  const renderEventCard = (event) => {
    const isVirtual = event.event_type === 'virtual'
    const isEventPast = event.category === 'past'
    const eventImage = event.banner_url || event.cover_image || '/Line4.png'

    return (
      <div
        key={event.id}
        className="relative rounded-[20px] overflow-hidden h-[280px] cursor-pointer hover:shadow-xl transition-all duration-300 outline outline-1 outline-[#E2E8F0]"
      >
        {/* Event Image Background */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src={eventImage}
            alt={event.title}
            className="w-full h-full object-cover"
            style={{ minHeight: '280px', minWidth: '100%' }}
          />
        </div>

        {/* Status Badge - Top Left */}
        <div className="absolute top-4 left-4 z-20">
          {getStatusBadge(event.status, isEventPast)}
        </div>

        {/* FROSTED GLASS - Purely visual layer */}
        <div className="absolute bottom-0 left-0 right-0 h-[126px] z-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              filter: 'blur(30px)',
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)'
            }}
          ></div>
          <div
            className="absolute inset-0"
            style={{
              backdropFilter: 'blur(30px)',
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 100%)'
            }}
          ></div>
        </div>

        {/* TEXT CONTENT - Blends with IMAGE (z-20) */}
        <div
          className="absolute bottom-0 left-0 right-0 h-[126px] z-20 px-5 py-4 flex items-center"
          style={{ mixBlendMode: 'exclusion' }}
        >
          {/* Left side content - ONLY event name and date */}
          <div className="flex-1 min-w-0 pr-4">
            {/* Event Name */}
            <h3 className="font-semibold text-white text-xl mb-0 leading-tight truncate">
              {event.title}
            </h3>

            {/* Date & Time */}
            <p className="text-white text-xs mb-0 font-light">
              {new Date(event.start_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              })} • {event.start_time}
            </p>

            {/* Spacer for badge + location */}
            <div className="h-[56px]"></div>
          </div>
        </div>

        {/* BADGE, LOCATION AND BUTTON - Separate layer, NO blend (z-30) */}
        <div className="absolute bottom-0 left-0 right-0 h-[126px] z-30 px-5 py-4 flex items-center pointer-events-none">
          <div className="flex-1 min-w-0 pr-4">
            {/* Spacer for title and date */}
            <div className="h-[32px]"></div>
            
            {/* Event Type Badge - NOT affected by blend mode */}
            <div className="mb-2 mt-2 pointer-events-auto">
              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-normal bg-[#EAF0FF] text-[#2248B2]">
                {isVirtual ? 'Virtual event' : 'Physical event'}
              </span>
            </div>

            {/* Location - NOT affected by blend mode */}
            <div className="flex items-center gap-1.5 pointer-events-auto">
              <MapPin size={14} weight="fill" className="text-white flex-shrink-0" />
              <span className="text-sm text-white font-medium truncate">
                {isVirtual ? (event.meeting_link || 'No link provided') : (event.venue || 'No venue')}
              </span>
            </div>
          </div>

          {/* Liquid Glass Arrow Button - NOT affected by blend mode */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/member/events/${event.id}`)
            }}
            className="liquid-glass-button w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 pointer-events-auto"
          >
            <ArrowUpRight size={32} weight="light" className="text-white" />
          </button>
        </div>
      </div>
    )
  }

  const filterEventsByMonth = (events) => {
    return events.filter(event => {
      const eventDate = new Date(event.start_date)
      return eventDate.getMonth() === selectedMonth && eventDate.getFullYear() === selectedYear
    })
  }

  const getEventsForTab = () => {
    let events = []
    if (activeTab === 'all') {
      events = [...upcomingEvents, ...activeEvents, ...pastEvents]
    } else if (activeTab === 'upcoming') {
      events = upcomingEvents
    } else if (activeTab === 'active') {
      events = activeEvents
    } else {
      events = pastEvents
    }

    // Apply month filter
    return filterEventsByMonth(events)
  }

  const getEmptyMessage = () => {
    if (activeTab === 'all') return 'No events'
    if (activeTab === 'upcoming') return 'No upcoming events'
    if (activeTab === 'active') return 'No events happening right now'
    return 'No past events'
  }

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  if (loading) {
    return <LoadingSpinner message="Loading events..." />
  }

  const currentEvents = getEventsForTab()

  return (
    <>
      <style>{`
        .liquid-glass-button {
          border-radius: 200px;
          height: 64px;
          width: 64px;
          backdrop-filter: blur(10px); 
          box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.20) inset, 0 1px 2px 0 rgba(255, 255, 255, 0.40) inset;
          background: #2C5DE5;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
      `}</style>

      <div className="min-h-screen bg-white pb-20">
        {/* Header */}
        <div className="px-6 pt-10 pb-6 flex items-start justify-between">
          <h1 className="text-2xl font-semibold text-slate-800">My events</h1>
          <div className="bg-slate-50 border border-slate-200 rounded-[20px] p-2">
            <Bell size={20} weight="fill" className="text-[#64748B]" />
          </div>
        </div>

        {/* Two Dropdowns Side by Side */}
        <div className="px-6 mb-6 flex items-center gap-1">
          {/* Month and Year selector */}
          <div className="bg-slate-50 border border-slate-200 rounded-[20px] p-1">
            <div className="relative">
              <select
                value={`${selectedYear}-${selectedMonth}`}
                onChange={(e) => {
                  const [year, month] = e.target.value.split('-')
                  setSelectedYear(parseInt(year))
                  setSelectedMonth(parseInt(month))
                }}
                className="appearance-none bg-transparent px-2 py-1 pr-6 rounded-[20px] text-xs font-normal text-slate-800 focus:outline-none cursor-pointer"
              >
                {Array.from({ length: 25 }, (_, i) => {
                  const date = new Date()
                  date.setMonth(date.getMonth() - 12 + i)
                  return (
                    <option key={i} value={`${date.getFullYear()}-${date.getMonth()}`}>
                      {months[date.getMonth()]}, {date.getFullYear()}
                    </option>
                  )
                })}
              </select>
              <CaretDown size={12} weight="regular" className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-800" />
            </div>
          </div>

          {/* Status selector */}
          <div className="bg-slate-50 border border-slate-200 rounded-[20px] p-1">
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="appearance-none bg-transparent px-2 py-1 pr-6 rounded-[20px] text-xs font-normal text-slate-800 focus:outline-none cursor-pointer"
              >
                <option value="upcoming">Upcoming</option>
                <option value="active">Active</option>
                <option value="past">Past</option>
                <option value="all">All</option>
              </select>
              <CaretDown size={12} weight="regular" className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-800" />
            </div>
          </div>
        </div>

        {/* Events list */}
        <div className="px-6">
          {currentEvents.length === 0 ? (
            <div className="bg-white rounded-[20px] border border-slate-200 p-8 text-center">
              <div className="bg-slate-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CalendarBlank size={32} weight="light" className="text-slate-400" />
              </div>
              <p className="text-slate-500 font-medium mb-1">{getEmptyMessage()}</p>
              <p className="text-sm text-slate-400">for {months[selectedMonth]} {selectedYear}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {currentEvents.map(event => renderEventCard(event))}
            </div>
          )}
        </div>
      </div>

      <MemberBottomNav />
    </>
  )
}